import React, { useState, useRef, useEffect } from 'react';
import Tree from 'react-d3-tree';

export default function Add({ facts, onSave, onCancel, initialData }) {
    const [outcome, setOutcome] = useState('');
    const [selectedNode, setSelectedNode] = useState('root'); // Start with root selected
    const [factValue, setFactValue] = useState('');
    const [selectedFact, setSelectedFact] = useState('');
    const [selectedOperator, setSelectedOperator] = useState('equal');
    const [selectedLogicalOperator, setSelectedLogicalOperator] = useState('all');
    const [isEditMode, setIsEditMode] = useState(false);
    const [viewPreview, setViewPreview] = useState(false);
    const [showRules, setshowRules] = useState(false);
    // Reference to the tree container for dimensions
    const treeContainerRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 400 });

    // Initialize tree with a root node
    const [treeData, setTreeData] = useState({
        id: 'root',
        name: 'all',
        type: 'operator',
        nodeSvgShape: { shape: 'circle', shapeProps: { r: 15, fill: '#2196F3' } },
        children: []
    });

    // Track node paths for easier manipulation
    const [nodePaths, setNodePaths] = useState({});

    // Initialize from initialData if in edit mode
    useEffect(() => {
        if (initialData) {
            setIsEditMode(true);
            setOutcome(initialData.Outcome);

            // Convert the rule condition back to tree structure
            const convertConditionToTree = (condition) => {
                if (!condition) return null;

                // If it has 'all' or 'any' keys, it's an operator node
                if (condition.all || condition.any) {
                    const operatorType = condition.all ? 'all' : 'any';
                    const children = condition[operatorType];

                    return {
                        id: 'root',
                        name: operatorType,
                        type: 'operator',
                        nodeSvgShape: {
                            shape: 'circle',
                            shapeProps: {
                                r: 15,
                                fill: operatorType === 'all' ? '#2196F3' : '#FF9800'
                            }
                        },
                        children: children.map((child, index) => {
                            const childTree = convertConditionToTree(child);
                            if (!childTree) return null;

                            // For leaf conditions
                            if (child.fact && child.operator) {
                                const factObj = facts.find(f => f.factName === child.fact);
                                let displayValue = child.value;

                                if (factObj && factObj.type === 'boolean') {
                                    displayValue = child.value ? 'TRUE' : 'FALSE';
                                } else if (factObj && factObj.type === 'array') {
                                    displayValue = Array.isArray(child.value) ?
                                        `[${child.value.join(', ')}]` : displayValue;
                                } else if (factObj && factObj.type === 'object') {
                                    displayValue = typeof child.value === 'object' ?
                                        JSON.stringify(child.value).substring(0, 20) + '...' : displayValue;
                                }

                                return {
                                    id: generateNodeId(),
                                    name: `${child.fact} ${getOperatorDisplay(child.operator)} ${displayValue}`,
                                    type: 'condition',
                                    factName: child.fact,
                                    factType: factObj?.type || 'string',
                                    operator: child.operator,
                                    value: child.value,
                                    nodeSvgShape: { shape: 'circle', shapeProps: { r: 15, fill: '#90CAF9' } }
                                };
                            }

                            // Give new id to non-root operator nodes
                            if (childTree.id === 'root' && index > 0) {
                                childTree.id = generateNodeId();
                            }

                            return childTree;
                        }).filter(Boolean)
                    };
                }

                // For leaf conditions at the root
                if (condition.fact && condition.operator) {
                    const factObj = facts.find(f => f.factName === condition.fact);

                    // Create root node with one child
                    return {
                        id: 'root',
                        name: 'all', // Default to 'all' for single conditions
                        type: 'operator',
                        nodeSvgShape: { shape: 'circle', shapeProps: { r: 15, fill: '#2196F3' } },
                        children: [{
                            id: generateNodeId(),
                            name: `${condition.fact} ${getOperatorDisplay(condition.operator)} ${condition.value}`,
                            type: 'condition',
                            factName: condition.fact,
                            factType: factObj?.type || 'string',
                            operator: condition.operator,
                            value: condition.value,
                            nodeSvgShape: { shape: 'circle', shapeProps: { r: 15, fill: '#90CAF9' } }
                        }]
                    };
                }

                return null;
            };

            const treeStructure = convertConditionToTree(initialData.condition);
            if (treeStructure) {
                setTreeData(treeStructure);
            }
        }
    }, [initialData, facts]);

    // Update dimensions on mount
    useEffect(() => {
        setNodePaths({ 'root': [] });
        if (treeContainerRef.current) {
            setDimensions({
                width: treeContainerRef.current.offsetWidth,
                height: 400
            });
        }

        // Update dimensions when window is resized
        const handleResize = () => {
            if (treeContainerRef.current) {
                setDimensions({
                    width: treeContainerRef.current.offsetWidth,
                    height: 400
                });
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const operators = {
        string: ['equal', 'notEqual', 'contains', 'doesNotContain'],
        number: ['equal', 'notEqual', 'greaterThan', 'lessThan', 'greaterThanInclusive', 'lessThanInclusive'],
        boolean: ['equal', 'notEqual'],
        array: ['contains', 'doesNotContain'],
        object: ['equal', 'notEqual']
    };

    const getOperatorDisplay = (op) => {
        const displayMap = {
            'equal': '=',
            'notEqual': '≠',
            'greaterThan': '>',
            'lessThan': '<',
            'greaterThanInclusive': '≥',
            'lessThanInclusive': '≤',
            'contains': 'contains',
            'doesNotContain': 'does not contain'
        };
        return displayMap[op] || op;
    };

    // Find a node in the tree by path
    const findNodeByPath = (path, currentNode = treeData) => {
        if (!path || path.length === 0) return currentNode;

        const [index, ...rest] = path;
        if (!currentNode.children || !currentNode.children[index]) return null;

        return findNodeByPath(rest, currentNode.children[index]);
    };

    // Generate a unique ID for nodes
    const generateNodeId = () => `node-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    const processFactValue = (value, factType) => {
        switch (factType) {
            case 'string':
                return String(value);

            case 'number':
                const num = Number(value);
                if (isNaN(num)) {
                    console.warn('Invalid number value:', value);
                    return 0;
                }
                return num;

            case 'boolean':
                if (typeof value === 'string') {
                    const lowercased = value.toLowerCase().trim();
                    if (lowercased === 'true' || lowercased === 'yes' || lowercased === '1') {
                        return true;
                    }
                    if (lowercased === 'false' || lowercased === 'no' || lowercased === '0') {
                        return false;
                    }
                    if (lowercased === 'on') return true;
                    if (lowercased === 'off') return false;
                }
                return Boolean(value);

            case 'array':
                if (typeof value === 'string') {
                    return value.split(',').map(item => item.trim()).filter(Boolean);
                }
                if (Array.isArray(value)) {
                    return value;
                }
                return [value];

            case 'object':
                if (typeof value === 'string') {
                    try {
                        return JSON.parse(value);
                    } catch (e) {
                        console.warn('Invalid JSON for object fact:', value);
                        return {};
                    }
                }
                if (typeof value === 'object' && value !== null) {
                    return value;
                }
                return {};

            default:
                return String(value);
        }
    };

    const handleAddCondition = () => {
        if (!selectedFact || !selectedOperator || !selectedNode) return;

        const factObj = facts.find(f => f.id === selectedFact);
        if (!factObj) return;

        const processedValue = processFactValue(factValue, factObj.type);

        let displayValue = factValue;
        if (factObj.type === 'boolean') {
            displayValue = processedValue ? 'TRUE' : 'FALSE';
        } else if (factObj.type === 'array') {
            displayValue = Array.isArray(processedValue) ?
                `[${processedValue.join(', ')}]` : displayValue;
        } else if (factObj.type === 'object') {
            displayValue = typeof processedValue === 'object' ?
                JSON.stringify(processedValue).substring(0, 20) + '...' : displayValue;
        }

        const newNode = {
            id: generateNodeId(),
            name: `${factObj.factName} ${getOperatorDisplay(selectedOperator)} ${displayValue}`,
            type: 'condition',
            factName: factObj.factName,
            factType: factObj.type,
            operator: selectedOperator,
            value: processedValue,
            nodeSvgShape: { shape: 'circle', shapeProps: { r: 15, fill: '#90CAF9' } }
        };

        const newTreeData = JSON.parse(JSON.stringify(treeData));

        const nodePath = nodePaths[selectedNode];
        const node = findNodeByPath(nodePath, newTreeData);

        if (node && node.type === 'operator') {
            if (!node.children) node.children = [];

            const safeNode = { ...newNode };

            if (safeNode.factType === 'object' || safeNode.factType === 'array') {
                safeNode._originalValue = safeNode.value;
                safeNode.value = JSON.stringify(safeNode.value);
            }

            node.children.push(safeNode);

            setTreeData(newTreeData);

            const newNodePath = [...nodePath, node.children.length - 1];
            setNodePaths(prevPaths => ({
                ...prevPaths,
                [newNode.id]: newNodePath
            }));

            setSelectedFact('');
            setFactValue('');
        }
    };

    // Add a nested logical operator (ALL/ANY) to the selected node
    const handleAddNestedOperator = () => {
        if (!selectedNode) return;

        const newNode = {
            id: generateNodeId(),
            name: selectedLogicalOperator,
            type: 'operator',
            nodeSvgShape: {
                shape: 'circle',
                shapeProps: {
                    r: 15,
                    fill: selectedLogicalOperator === 'all' ? '#2196F3' : '#FF9800'
                }
            },
            children: []
        };

        const newTreeData = JSON.parse(JSON.stringify(treeData));

        const nodePath = nodePaths[selectedNode];
        const node = findNodeByPath(nodePath, newTreeData);

        if (node && node.type === 'operator') {
            if (!node.children) node.children = [];
            node.children.push(newNode);

            setTreeData(newTreeData);

            const newNodePath = [...nodePath, node.children.length - 1];
            setNodePaths(prevPaths => ({
                ...prevPaths,
                [newNode.id]: newNodePath
            }));

            setSelectedNode(newNode.id);
        }
    };

    // Remove a node from the tree
    const handleRemoveNode = () => {
        if (!selectedNode || selectedNode === 'root') {
            if (selectedNode === 'root') {
                alert('Cannot remove the root node');
            }
            return;
        }

        const nodePath = nodePaths[selectedNode];
        if (!nodePath || nodePath.length === 0) return;

        const newTreeData = JSON.parse(JSON.stringify(treeData));

        const parentPath = nodePath.slice(0, -1);
        const parent = findNodeByPath(parentPath, newTreeData);

        if (parent && parent.children) {
            const nodeIndex = nodePath[nodePath.length - 1];

            const nodeToRemove = parent.children[nodeIndex];
            if (nodeToRemove.children && nodeToRemove.children.length > 0) {
                const confirmRemove = window.confirm('This node has children. Removing it will remove all its children. Continue?');
                if (!confirmRemove) return;
            }

            parent.children.splice(nodeIndex, 1);

            setTreeData(newTreeData);

            setSelectedNode(parent.id);

            rebuildNodePaths(newTreeData);
        }
    };

    // Helper function to rebuild all node paths
    const rebuildNodePaths = (treeData) => {
        const buildPaths = (node, path = []) => {
            if (!node) return {};

            let paths = {};
            if (node.id) {
                paths[node.id] = [...path];
            }

            if (node.children) {
                node.children.forEach((child, index) => {
                    const childPaths = buildPaths(child, [...path, index]);
                    paths = { ...paths, ...childPaths };
                });
            }

            return paths;
        };

        setNodePaths(buildPaths(treeData));
    };

    // Convert tree data to json-rules-engine format
    const convertTreeToRuleFormat = (node = treeData) => {
        // For condition nodes, create a proper condition object
        if (node.type === 'condition') {
            if (!node.factName) {
                console.error('Missing factName for condition:', node);
                return null;
            }

            let finalValue = node.value;

            if (node._originalValue !== undefined) {
                finalValue = node._originalValue;
            }
            else if (node.factType && typeof node.value === 'string') {
                if (node.factType === 'object') {
                    try {
                        finalValue = JSON.parse(node.value);
                    } catch (e) {
                        console.warn('Error parsing object value:', node.value);
                    }
                } else if (node.factType === 'array') {
                    try {
                        finalValue = JSON.parse(node.value);
                        if (!Array.isArray(finalValue)) {
                            finalValue = node.value.split(',').map(item => item.trim());
                        }
                    } catch (e) {
                        finalValue = node.value.split(',').map(item => item.trim());
                    }
                } else if (node.factType === 'boolean') {
                    if (node.value.toLowerCase() === 'true') finalValue = true;
                    else if (node.value.toLowerCase() === 'false') finalValue = false;
                } else if (node.factType === 'number') {
                    finalValue = Number(node.value);
                }
            }

            return {
                fact: node.factName,
                operator: node.operator,
                value: finalValue
            };
        }

        // For operator nodes (all/any), create a proper group condition
        if (node.type === 'operator' && node.children && node.children.length > 0) {
            const validChildren = node.children
                .map(child => convertTreeToRuleFormat(child))
                .filter(Boolean);

            if (validChildren.length > 0) {
                return {
                    [node.name]: validChildren
                };
            }
            return null;
        }

        return null;
    };

    // Handle saving the rule
    const handleSave = () => {
        if (!outcome || treeData.children.length === 0) {
            alert('Please add an outcome and at least one condition');
            return;
        }

        const conditionObj = convertTreeToRuleFormat();

        if (!conditionObj) {
            alert('Invalid rule structure. Please check your conditions.');
            return;
        }

        const validateConditionStructure = (condition) => {
            if (condition.all || condition.any) {
                const children = condition.all || condition.any;
                if (!Array.isArray(children) || children.length === 0) {
                    return false;
                }
                return children.every(child => validateConditionStructure(child));
            }

            return condition.fact && condition.operator && 'value' in condition;
        };

        if (!validateConditionStructure(conditionObj)) {
            alert('Rule structure is invalid. Make sure all conditions have fact, operator, and value properties.');
            return;
        }

        const countConditions = (node) => {
            if (!node.children) return 0;

            let count = 0;
            for (const child of node.children) {
                if (child.type === 'condition') {
                    count++;
                } else if (child.type === 'operator') {
                    count += countConditions(child);
                }
            }
            return count;
        };

        const totalConditions = countConditions(treeData);

        onSave({
            Outcome: outcome,
            condition: conditionObj,
            conditionDisplay: `${treeData.name.toUpperCase()}: ${totalConditions} condition(s)`
        });
    };

    // Custom tree node renderer with improved display and interaction
    const renderForeignObjectNode = ({ nodeDatum }) => {
        let bgColor = '#F5F5F5';
        let circleColor = '#90CAF9';

        if (nodeDatum.name === 'all') {
            bgColor = '#E3F2FD';
            circleColor = '#2196F3';
        } else if (nodeDatum.name === 'any') {
            bgColor = '#FFF3E0';
            circleColor = '#FF9800';
        } else if (nodeDatum.name.startsWith('Outcome')) {
            bgColor = '#E8F5E9';
            circleColor = '#8BC34A';
        }

        if (selectedNode === nodeDatum.id) {
            bgColor = '#FFECB3';
        }

        return (
            <g>
                <circle
                    r={15}
                    fill={circleColor}
                    onClick={() => {
                        if (nodeDatum.id) {
                            setSelectedNode(nodeDatum.id === selectedNode ? null : nodeDatum.id);
                        }
                    }}
                    style={{ cursor: 'pointer' }}
                />
                <foreignObject width={200} height={100} x={-100} y={-20}>
                    <div
                        style={{
                            backgroundColor: bgColor,
                            padding: '5px',
                            borderRadius: '8px',
                            border: selectedNode === nodeDatum.id ? '2px solid #FFC107' : '1px solid #ccc',
                            textAlign: 'center',
                            cursor: 'pointer'
                        }}
                        onClick={() => {
                            if (nodeDatum.id) {
                                setSelectedNode(nodeDatum.id === selectedNode ? null : nodeDatum.id);
                            }
                        }}
                    >
                        <div style={{ fontWeight: 'bold' }}>{nodeDatum.name}</div>
                        {nodeDatum.type === 'operator' &&
                            <div style={{ fontSize: '0.8rem', marginTop: '2px' }}>
                                {nodeDatum.children?.length || 0} child node(s)
                            </div>
                        }
                    </div>
                </foreignObject>
            </g>
        );
    };

    // Initialize node paths when tree data changes
    useEffect(() => {
        const buildNodePaths = (node, path = []) => {
            if (!node) return {};

            let paths = {};
            if (node.id) {
                paths[node.id] = [...path];
            }

            if (node.children) {
                node.children.forEach((child, index) => {
                    const childPaths = buildNodePaths(child, [...path, index]);
                    paths = { ...paths, ...childPaths };
                });
            }

            return paths;
        };

        const treeWithId = { ...treeData };
        if (!treeWithId.id) {
            treeWithId.id = 'root';
        }

        setNodePaths(buildNodePaths(treeWithId));
    }, [treeData]);

    // Add outcome node to visualization
    const treeDataWithOutcome = React.useMemo(() => {
        if (!outcome) return treeData;

        const newTreeData = JSON.parse(JSON.stringify(treeData));

        const hasOutcomeNode = newTreeData.children && newTreeData.children.some(
            child => child.type === 'outcome'
        );

        if (!hasOutcomeNode) {
            if (!newTreeData.children) newTreeData.children = [];

            newTreeData.children.push({
                id: 'outcome-node',
                name: `Outcome: ${outcome}`,
                type: 'outcome',
                nodeSvgShape: { shape: 'circle', shapeProps: { r: 15, fill: '#8BC34A' } }
            });
        } else {
            const outcomeIndex = newTreeData.children.findIndex(
                child => child.type === 'outcome'
            );

            if (outcomeIndex !== -1) {
                newTreeData.children[outcomeIndex].name = `Outcome: ${outcome}`;
            }
        }

        return newTreeData;
    }, [treeData, outcome]);

    // Get node path display for UI
    const getNodePathDisplay = (nodeId) => {
        if (!nodeId) return '';

        const path = nodePaths[nodeId];
        if (!path) return '';

        let currentNode = treeData;
        let pathDisplay = currentNode.name;

        for (const index of path) {
            if (!currentNode.children || !currentNode.children[index]) break;
            currentNode = currentNode.children[index];
            pathDisplay += ` > ${currentNode.name}`;
        }

        return pathDisplay;
    };

    // Get info about currently selected node
    const getSelectedNodeInfo = () => {
        if (!selectedNode) return null;
        return findNodeByPath(nodePaths[selectedNode], treeData);
    };

    const selectedNodeInfo = getSelectedNodeInfo();

    // Get fact type for selected fact
    const getSelectedFactType = () => {
        if (!selectedFact) return null;
        const factObj = facts.find(f => f.id === selectedFact);
        return factObj ? factObj.type : null;
    };

    const selectedFactType = getSelectedFactType();

    // Get display value for boolean type input
    const getBooleanDisplayValue = (value) => {
        if (value === true || value === 'true' || value === 'TRUE') return 'TRUE';
        if (value === false || value === 'false' || value === 'FALSE') return 'FALSE';
        return '';
    };

    return (
        <>
            <div className='Add-decision'>
                <div className="row">
                    <div className="col-sm-4 col-xs-12">
                        <div className="topBlock">
                            <div className="blocks">
                                <h6 style={{ fontSize: '14px' }}>{isEditMode ? 'Edit Decision Rule' : 'Step 1: Configure Rule Structure'}</h6>

                                {treeData.children.length === 0 && (
                                    <div className='btns'>
                                        <div className="grp left-border" style={{ width: '50%' }}>
                                            <button
                                                className={`decisionbtn left-border ${treeData.name === 'all' ? 'active' : ''}`}
                                                onClick={() => {
                                                    setTreeData({
                                                        ...treeData,
                                                        id: treeData.id || 'root',
                                                        name: 'all',
                                                        type: 'operator',
                                                        nodeSvgShape: {
                                                            shape: 'circle',
                                                            shapeProps: { r: 15, fill: '#2196F3' }
                                                        }
                                                    });
                                                }}
                                                style={{ padding: '8px 10px', width: '100%' }}
                                            >
                                                All (AND)
                                            </button>
                                        </div>
                                        <div className="grp right-border" style={{ width: '50%' }}>
                                            <button
                                                className={`decisionbtn right-border ${treeData.name === 'any' ? 'active' : ''}`}
                                                onClick={() => {
                                                    setTreeData({
                                                        ...treeData,
                                                        id: treeData.id || 'root',
                                                        name: 'any',
                                                        type: 'operator',
                                                        nodeSvgShape: {
                                                            shape: 'circle',
                                                            shapeProps: { r: 15, fill: '#FF9800' }
                                                        }
                                                    });
                                                }}
                                                style={{ padding: '8px 10px', width: '100%' }}
                                            >
                                                Any (OR)
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {selectedNode && (
                                    <div className="selected-node-info " style={{ marginTop: '14px' }}>
                                        <div className="alert alert-info">
                                            <strong>Selected Node:</strong> {selectedNodeInfo?.name || 'None'}
                                            <div className="" style={{ fontSize: '12.8px', marginTop: '8px' }}>
                                                <strong>Path:</strong> {getNodePathDisplay(selectedNode)}
                                            </div>
                                            <div className="" style={{ fontSize: '12.8px', marginTop: '8px' }}>
                                                <strong>Type:</strong> {selectedNodeInfo?.type === 'operator' ?
                                                    `Logical Operator (${selectedNodeInfo?.name.toUpperCase()})` :
                                                    selectedNodeInfo?.type}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedNode && (
                                    <div className="node-actions " style={{ marginTop: '12px' }}>
                                        <div className="row">
                                            <div className="col-12 " style={{ marginBottom: '12px', padding: '0 15px' }}>
                                                <button
                                                    className="btn btn-outline-danger btn-sm w-100"
                                                    onClick={handleRemoveNode}
                                                    disabled={selectedNode === 'root'}
                                                    style={{ color: '#dc3545', background: 'transparent', borderColor: '#dc3545', opacity: '0.65', whiteSpace: 'break-spaces', fontSize: '14px', width: '100%' }}
                                                >
                                                    <i className="fa fa-trash"></i> Remove Node
                                                    {selectedNode === 'root' && " (Root cannot be removed)"}
                                                </button>
                                            </div>

                                            {selectedNodeInfo?.type === 'operator' && (
                                                <div className="col-12" style={{ margin: '0 15px' }}>
                                                    <div className="card" style={{ border: '1px solid rgba(0, 0, 0, 0.175)', borderRadius: '6px', padding: '0', whiteSpace: 'break-spaces' }}>
                                                        <div className="card-header" style={{ background: 'rgba(0, 0, 0, 0.03)', padding: '8px 16px', borderBottom: '1px solid rgba(0,0,0,0.175)' }}>
                                                            <h6 className="" style={{ margin: '0', fontSize: '16px', textAlign: 'center' }}>Add Nested Operator</h6>
                                                        </div>
                                                        <div className="card-body" style={{ padding: '16px', background: '#fff', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px' }}>
                                                            <div className='btns'>
                                                                <div className="grp left-border" style={{ width: '50%' }}>
                                                                    <button
                                                                        className={`decisionbtn left-border ${selectedLogicalOperator === 'all' ? 'active' : ''}`}
                                                                        onClick={() => setSelectedLogicalOperator('all')}
                                                                        style={{ textAlign: 'center', width: '100%', padding: '8px 0' }}
                                                                    >
                                                                        All (AND)
                                                                    </button>
                                                                </div>
                                                                <div className="grp right-border" style={{ width: '50%' }}>
                                                                    <button
                                                                        className={`decisionbtn right-border ${selectedLogicalOperator === 'any' ? 'active' : ''}`}
                                                                        onClick={() => setSelectedLogicalOperator('any')}
                                                                        style={{ textAlign: 'center', width: '100%', padding: '8px 0' }}
                                                                    >
                                                                        Any (OR)
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <button
                                                                className="addbtn" style={{ marginTop: '15px', width: '100%' }}
                                                                onClick={handleAddNestedOperator}
                                                            >
                                                                Add Nested Operator
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="blocks">
                                <h6 style={{ fontSize: '14px' }}>Step 2: Add Conditions</h6>

                                {selectedNodeInfo?.type === 'operator' && (
                                    <div className="condition-form" style={{ marginTop: '16px' }}>
                                        <div className="form-group">
                                            <label>Select Fact</label>
                                            <select
                                                className="form-control"
                                                value={selectedFact}
                                                onChange={e => {
                                                    setSelectedFact(e.target.value);
                                                    // Reset value when fact changes
                                                    setFactValue('');
                                                    // Reset operator when fact changes
                                                    const newFactType = facts.find(f => f.id === e.target.value)?.type || 'string';
                                                    setSelectedOperator(operators[newFactType][0]);
                                                }}
                                            >
                                                <option value="">-- Select Fact --</option>
                                                {facts.map(fact => (
                                                    <option key={fact.id} value={fact.id}>
                                                        {fact.factName} ({fact.type})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {selectedFact && (
                                            <>
                                                <div className="form-group " style={{ marginTop: '14px' }}>
                                                    <label>Operator</label>
                                                    <select
                                                        className="form-control"
                                                        value={selectedOperator}
                                                        onChange={e => setSelectedOperator(e.target.value)}
                                                    >
                                                        {operators[selectedFactType]?.map(op => (
                                                            <option key={op} value={op}>
                                                                {getOperatorDisplay(op)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="form-group " style={{ marginTop: '14px' }}>
                                                    <label>Value</label>
                                                    {selectedFactType === 'boolean' ? (
                                                        <select
                                                            className="form-control"
                                                            value={factValue}
                                                            onChange={e => setFactValue(e.target.value === 'true')}
                                                        >
                                                            <option value="">-- Select Value --</option>
                                                            <option value="true">TRUE</option>
                                                            <option value="false">FALSE</option>
                                                        </select>
                                                    ) : (
                                                        <input
                                                            type={selectedFactType === 'number' ? 'number' : 'text'}
                                                            className="form-control"
                                                            value={factValue}
                                                            onChange={e => setFactValue(e.target.value)}
                                                            placeholder={`Enter ${selectedFactType} value...`}
                                                            style={{ fontSize: '0.9rem' }}
                                                        />
                                                    )}

                                                    {selectedFactType === 'array' && (
                                                        <small className="form-text text-muted">
                                                            Enter comma-separated values
                                                        </small>
                                                    )}

                                                    {selectedFactType === 'object' && (
                                                        <small className="form-text text-muted">
                                                            Enter valid JSON object
                                                        </small>
                                                    )}
                                                </div>

                                                <button
                                                    className="addbtn mt-3"
                                                    style={{ marginTop: '16px' }}
                                                    onClick={handleAddCondition}
                                                    disabled={!factValue && selectedFactType !== 'boolean'}
                                                >
                                                    Add Condition
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="blocks">
                                <h6 style={{ fontSize: '14px' }}>Step 3: Set Outcome</h6>
                                <div className="form-group mt-3">
                                    <label>Decision Outcome</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter outcome value..."
                                        value={outcome}
                                        onChange={e => setOutcome(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-sm-8 col-xs-12">
                        <div className="topBlock">
                            <div className="blocks">
                                <h6 style={{ fontSize: '14px' }}>Rule Visualization</h6>
                                <div
                                    id="treeWrapper"
                                    style={{ width: '100%', height: '400px' }}
                                    ref={treeContainerRef}
                                >
                                    <Tree
                                        data={treeDataWithOutcome}
                                        orientation="vertical"
                                        collapsible={false}
                                        nodeSize={{ x: 250, y: 100 }}
                                        renderCustomNodeElement={renderForeignObjectNode}
                                        separation={{ siblings: 2, nonSiblings: 2 }}
                                        translate={{ x: dimensions.width / 2, y: 50 }}
                                        zoomable={true}
                                        zoom={0.8}
                                    />
                                </div>
                            </div>

                            <div className="blocks">
                                <h6 onClick={() => setViewPreview(!viewPreview)} style={{ cursor: 'pointer', display: 'inline-block', fontSize: '14px' }}>Rule Preview&nbsp;
                                    <span>
                                        {
                                            viewPreview ?
                                                <i class="fa fa-caret-up" aria-hidden="true"></i>
                                                :
                                                <i class="fa fa-caret-down" aria-hidden="true"></i>
                                        }
                                    </span>
                                </h6>
                                {
                                    viewPreview ?
                                        <div className="rule-preview p-3 mt-2" style={{ backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                                            <div><strong>Outcome:</strong> {outcome || '(Not set)'}</div>
                                            <div className="mt-2">
                                                <strong>Conditions:</strong>
                                                <pre style={{ marginTop: '8px', fontSize: '0.85rem' }}>
                                                    {JSON.stringify(convertTreeToRuleFormat(), null, 2)}
                                                </pre>
                                            </div>
                                        </div>
                                        : ''}
                            </div>

                            <div className="action-buttons" style={{ margin: '10px 8px' }}>
                                <button

                                    className="btn btn-secondary mr-2"
                                    onClick={onCancel}
                                    style={{ marginRight: '10px', padding: '8px 12px', background: '#6c757d', color: '#fff' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    style={{ padding: '8px 12px' }}
                                    className="btn btn-primary"
                                    onClick={handleSave}
                                    disabled={!outcome || treeData.children.length === 0}
                                >
                                    {isEditMode ? 'Update Rule' : 'Save Rule'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Help section */}
                <div className="row" style={{ margin: '20px 0' }}>
                    <div className="col-12">
                        <div className="card" style={{ border: '1px solid rgba(0,0,0,0.175)', borderRadius: '6px', padding: '0px' }}>
                            <div className="card-header bg-info text-white" style={{ background: '#0dcaf0', borderBottom:  showRules ? '1px solid rgba(0,0,0,0.175)' : "none", padding: '8px 16px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px',borderBottomLeftRadius: showRules ? "0" : '6px', borderBottomRightRadius: showRules ? "0" : '6px', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setshowRules(!showRules)}>
                                <h6 className="" style={{ margin: '0', fontSize: '16px' }}>How to Build a Rule</h6>
                                <h6 style={{ margin: '0' }}>
                                    {
                                        showRules ?
                                            <i class="fa fa-chevron-up" aria-hidden="true"></i>
                                            :
                                            <i class="fa fa-chevron-down" aria-hidden="true"></i>
                                    }
                                </h6>
                            </div>
                            {
                                showRules ?

                                    <div className="card-body" style={{ padding: '16px', borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px' }}>
                                        <ol>
                                            <li style={{ fontSize: '16px' }}><strong>Start with a logical operator:</strong> Choose whether all conditions must match (AND) or any condition can match (OR).</li>
                                            <li style={{ fontSize: '16px' }}><strong>Add conditions:</strong> Select a node in the tree, then add conditions by selecting facts, operators, and values.</li>
                                            <li style={{ fontSize: '16px' }}><strong>Create nested logic:</strong> Add nested ALL/ANY operators to create complex condition groups.</li>
                                            <li style={{ fontSize: '16px' }}><strong>Set the outcome:</strong> Define what should happen when the rule conditions are met.</li>
                                            <li style={{ fontSize: '16px' }}><strong>Save your rule:</strong> Once your rule structure is complete, save it to add it to your ruleset.</li>
                                        </ol>
                                        <div className="alert alert-warning" style={{ marginTop: '12px' }}>
                                            <strong>Tip:</strong> Click on any node in the tree to select it before adding new conditions or operators.
                                        </div>
                                    </div>
                                    : ''}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}